// Pure mapping from a login-page `?reason=` query param to the short banner
// shown above the sign-in form, mirroring login-error-copy.js's shape.
//
// `inactivity` is the client-side idle timer's own reason (see
// shared/component/timeout.js -> useLogout('inactivity')). `idle` and `max`
// are set by the server (Task 9.3, SSO hardening): the auth service ends a
// session server-side once it has been idle past ADMIN_IDLE_TIMEOUT or older
// than ADMIN_SESSION_MAX, and the next token refresh comes back 401 with
// that reason -- api.service.js reads it off the response and forwards it
// here via loginUrlFor. `expired` is the same server-set shape for a refresh
// token that simply ran out: a plain refresh, or an expired /redirect/*
// deep link (recreate) -- see logout-destination.js's sessionEndReason.
// Unknown/absent reasons render nothing.
export const REASON_NOTICES = {
  inactivity: 'You were signed out due to inactivity. Please sign in again.',
  idle: 'You were signed out after a period of inactivity.',
  max: 'Your session reached its maximum length. Please sign in again.',
  expired: 'Your sign-in link expired. Sign in to continue.',
  // The session was ended from elsewhere (signed out, device revoked): the
  // refresh came back 401 with no reason of its own.
  ended: 'Your session ended. Please sign in again.',
  // An impersonated session ends 2 hours after the impersonation started
  // (auth answers the refresh 401 reason impersonation_limit).
  impersonation_limit: 'Impersonation ended — the 2-hour limit was reached.',
  // An invitation was accepted but signing in straight after failed
  // (view/invite/invite-register.js).
  'account-ready': 'Your account is ready. Sign in to continue.',
  // The organization gate (core orggate) answers 401 { code:
  // 'organization_unavailable' } when the session's organization was
  // scheduled for deletion -- sessionEndReason reads it off the response
  // and forwards it here via loginUrlFor, same as the other server-set
  // reasons. This is never a plain session expiry, so it says so.
  organization_unavailable: "Your organization isn't available any more, so you were signed out. Sign in again to continue in another organization.",
  // Require SSO ended the session (owner ruling 2026-10-29): auth answers
  // 401 reason sso_required with its own sentence naming the connection
  // ("Your organization now requires you to sign in with Contoso SSO."),
  // which the session's end stashes for this page (logout-destination.js
  // stashLogoutMessage). These words are only the fallback for an empty
  // sentence, a reload, or a link that names the reason.
  sso_required: 'Your organization now requires you to sign in with its single sign-on.'
}

// message: the server's own sentence, stashed when it ended the session.
// Only sso_required reads it; every other reason keeps its own words.
export const reasonNotice = (reason, message) => {
  if (reason === 'sso_required' && typeof message === 'string' && message.trim()) return message.trim()
  return REASON_NOTICES[reason] || null
}
